import { useEffect, useState } from 'react';
import { D, fmtAmt, type BalanceChangeRow } from '@wave/shared';
import { useBalanceChanges } from '../../api/hooks';
import { fmtAgo } from './historyMath';

/** localStorage-ключ развёрнутости блока — читается и DivergenceModal (кнопка «Открыть журнал»). */
export const LS_KEY = 'balances.changesLog.open';

/** id блока — цель `scrollIntoView` для кнопки «Открыть журнал» в DivergenceModal. */
export const CHANGES_LOG_ID = 'balances-changes-log';

/**
 * Событие «раскрыть блок сейчас» для уже смонтированного компонента —
 * `localStorage` сам по себе не триггерит ре-рендер (значение читается
 * только один раз, при инициализации `useState`), поэтому DivergenceModal
 * не может раскрыть уже отрисованный на странице блок одной лишь записью в
 * localStorage. См. `DivergenceModal.tsx`.
 */
export const CHANGES_LOG_OPEN_EVENT = 'wave:balances-changes-log-open';

/** Бейдж источника — стиль бейджей TG/TRON таблицы балансов (BalancesPage.tsx). */
export const SOURCE_BADGE: Record<BalanceChangeRow['source'], { label: string; title: string }> = {
  bot: { label: 'БОТ', title: 'Из журнала транзакций бота' },
  telegram: { label: 'TG', title: 'Из Telegram-события (распарсенное сообщение)' },
  tron: { label: 'TRON', title: 'Опрос кошелька через TronScan' },
};

/**
 * Окраска дельты — знаковая семантика страницы /balances, НЕ «плюс всегда зелёный»:
 * чаты ('bot'/'telegram') — обязательства, плюс = «мы должны больше» = красный,
 * минус = «нам должны» = зелёный; 'tron' — кошельки-активы, плюс = зелёный (пополнение),
 * минус = красный (списание). См. CLAUDE.md «UI цвета — приход/расход/нейтрал».
 */
export function deltaCls(source: BalanceChangeRow['source'], delta: string): string {
  const d = D(delta);
  if (d.isZero()) return 'text-text-subtle';
  const positive = d.isPositive();
  if (source === 'tron') return positive ? 'text-success' : 'text-danger';
  return positive ? 'text-danger' : 'text-success';
}

export function fmtDelta(row: BalanceChangeRow): string {
  const d = D(row.delta);
  // fmtAmt: CcyCode = string (packages/shared/src/currencies.ts) — принимает любую
  // строку, для незарегистрированных кодов (валюты бота вне CCY_CODES) откатывается
  // на сам код как символ и 2 знака после запятой. Падений на «чужих» валютах нет.
  // Код валюты уже встроен в fmtAmt (символ либо, для неизвестных кодов, сам код) —
  // не дублировать его следом (см. остальные call-sites fmtAmt в репо).
  return `${d.isNegative() ? '−' : '+'}${fmtAmt(d.abs().toString(), row.ccy)}`;
}

/** Сворачивающийся блок «Журнал изменений» — лента balance_changes (bot/telegram/tron). */
export function BalanceChangesLog() {
  const [open, setOpen] = useState(() => localStorage.getItem(LS_KEY) === '1');
  const q = useBalanceChanges();
  const items = q.data?.pages.flatMap((p) => p.items) ?? [];
  const last = items[0];

  useEffect(() => {
    function onOpenEvent() {
      setOpen(true);
      localStorage.setItem(LS_KEY, '1');
    }
    window.addEventListener(CHANGES_LOG_OPEN_EVENT, onOpenEvent);
    return () => window.removeEventListener(CHANGES_LOG_OPEN_EVENT, onOpenEvent);
  }, []);

  const toggle = () => {
    setOpen((v) => {
      localStorage.setItem(LS_KEY, v ? '0' : '1');
      return !v;
    });
  };

  return (
    <div id={CHANGES_LOG_ID} className="mb-4 rounded-lg border border-border bg-bg2">
      <button
        type="button"
        onClick={toggle}
        className="flex w-full flex-wrap items-center gap-x-2 gap-y-0.5 px-3 py-3 text-left sm:px-4"
      >
        <span className="text-sm font-medium text-text">Журнал изменений</span>
        {last && (
          <span className="text-xs text-text2">
            последнее — {fmtAgo(last.occurredAt, Date.now()) ?? 'только что'}
          </span>
        )}
        <span className="ml-auto text-text-subtle">{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <div className="border-t border-border px-3 py-2 sm:px-4">
          {items.length === 0 ? (
            <div className="py-4 text-center text-sm text-text-subtle">
              Изменений пока нет — лента копится с момента включения
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {items.map((row) => (
                <li key={row.id} className="flex items-start gap-2 py-1.5 text-sm sm:items-center sm:gap-2.5">
                  <span
                    className="w-14 shrink-0 text-xs text-text2 sm:w-16"
                    title={`${new Date(row.occurredAt).toLocaleString('ru-RU')}${
                      row.externalTime ? ` · время бота: ${row.externalTime}` : ''
                    }`}
                  >
                    {fmtAgo(row.occurredAt, Date.now()) ?? '—'}
                  </span>
                  <span
                    className="shrink-0 rounded border border-border bg-bg3 px-1 py-px text-[9px] font-medium uppercase tracking-wide text-text2"
                    title={SOURCE_BADGE[row.source].title}
                  >
                    {SOURCE_BADGE[row.source].label}
                  </span>
                  {/* Телефон: «кто» — второй строкой под названием (колонка w-32 не влезает);
                      название переносится, а не обрезается. */}
                  <div className="min-w-0 flex-1">
                    <div className="break-words text-text sm:truncate" title={row.chatId}>
                      {row.title}
                    </div>
                    <div className="break-words text-xs text-text2 sm:hidden">{row.actor ?? '—'}</div>
                  </div>
                  <span className={`shrink-0 font-mono ${deltaCls(row.source, row.delta)}`}>
                    {fmtDelta(row)}
                  </span>
                  <span
                    className="hidden w-32 shrink-0 truncate text-right text-xs text-text2 sm:block"
                    title={row.actor ?? undefined}
                  >
                    {row.actor ?? '—'}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {q.hasNextPage && (
            <div className="py-2 text-center">
              <button
                type="button"
                onClick={() => q.fetchNextPage()}
                disabled={q.isFetchingNextPage}
                className="text-xs font-medium text-accent hover:underline disabled:opacity-50"
              >
                {q.isFetchingNextPage ? 'Загрузка…' : 'Показать ещё'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
